import React, { useState, useEffect } from "react";
import { Button as ReactstrapButton } from "reactstrap";
import { Button, Tag, Space, Badge, message } from "antd";
import type { TableColumnsType } from "antd";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkRequestAside from "./workRequestAside";
import { 
  WorkRequestData, 
  WorkRequestStatus,
  WorkRequestFilters 
} from "./types";
import { 
  getWorkRequests, 
  updateWorkRequest, 
  deleteWorkRequest 
} from "./mockData/workRequestData";

const WorkRequestList: React.FC = () => {
  const [sm, setSm] = useState(false);
  const [workRequests, setWorkRequests] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<WorkRequestFilters>({});

  useEffect(() => {
    loadWorkRequests();
  }, [filters]);

  const loadWorkRequests = () => {
    setLoading(true);
    try {
      const data = getWorkRequests(filters);
      setWorkRequests(data);
    } catch (error) {
      console.error("Error loading work requests:", error);
      message.error("Failed to load work requests");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: WorkRequestStatus): string => {
    const statusColors: { [key in WorkRequestStatus]: string } = {
      "Draft": "default",
      "Pending Approval": "processing",
      "Approved": "success",
      "Rejected": "error",
      "In Progress": "warning",
      "Completed": "success",
    };
    return statusColors[status] || "default";
  };

  const getRequestForColor = (requestFor: string): string => {
    const colors: { [key: string]: string } = {
      "Construction Work": "blue",
      "Service": "green",
      "Maintenance": "orange",
      "Others": "purple"
    };
    return colors[requestFor] || "default";
  };

  const handleEdit = (record: WorkRequestData) => {
    // Navigate to edit mode
    window.location.href = `/work-request-creation?edit=${record.id}`;
  };

  const handleView = (record: WorkRequestData) => {
    // Navigate to view details
    window.location.href = `/work-request-view/${record.id}`;
  };

  const handleDuplicate = (record: WorkRequestData) => {
    // Navigate to duplicate mode
    window.location.href = `/work-request-creation?duplicate=${record.id}`;
  };

  const handleDelete = (record: WorkRequestData) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const success = deleteWorkRequest(record.id);
        if (success) {
          Swal.fire("Deleted!", "Work Request has been deleted.", "success");
          loadWorkRequests(); // Refresh the list
        } else {
          Swal.fire("Error!", "Failed to delete work request.", "error");
        }
      }
    });
  };

  const handleSubmitForApproval = (record: WorkRequestData) => {
    Swal.fire({
      title: "Submit for Approval",
      text: "Are you sure you want to submit this Work Request for approval?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, submit it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const updated = updateWorkRequest(record.id, {
          status: WorkRequestStatus.PendingApproval
        });
        if (updated) {
          Swal.fire("Submitted!", "Work Request has been submitted for approval.", "success");
          loadWorkRequests(); // Refresh the list
        } else {
          Swal.fire("Error!", "Failed to submit work request.", "error");
        }
      }
    });
  };

  const handleApprove = (record: WorkRequestData) => {
    Swal.fire({
      title: "Approve Work Request",
      input: "textarea",
      inputLabel: "Approval Comments (Optional)",
      inputPlaceholder: "Enter approval comments...",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        const updated = updateWorkRequest(record.id, {
          status: WorkRequestStatus.Approved,
          approverComments: result.value || undefined
        });
        if (updated) {
          Swal.fire("Approved!", "Work Request has been approved.", "success");
          loadWorkRequests();
        }
      }
    });
  };

  const handleReject = (record:any) => {
    // Swal.fire({
    //   title: "Reject Work Request",
    //   input: "textarea",
    //   inputLabel: "Rejection Reason (Required)",
    //   inputPlaceholder: "Enter reason for rejection...",
    //   inputValidator: (value) => {
    //     if (!value) {
    //       return "Rejection reason is required!";
    //     }
    //   },
    //   showCancelButton: true,
    //   confirmButtonText: "Reject",
    //   confirmButtonColor: "#dc3545",
    //   cancelButtonColor: "#6c757d",
    // }).then((result) => {
    //   if (result.isConfirmed) {
    //     const updated = updateWorkRequest(record.id, {
    //       status: WorkRequestStatus.Rejected,
    //       rejectionComments: result.value
    //     });
    //     if (updated) {
    //       Swal.fire("Rejected!", "Work Request has been rejected.", "success");
    //       loadWorkRequests();
    //     }
    //   }
    // });
  };

  const columns: TableColumnsType<WorkRequestData> = [
    {
      title: "WR #",
      dataIndex: "workRequestNumber",
      key: "workRequestNumber",
      width: 120,
      render: (text) => <strong>{text}</strong>,
      sorter: (a, b) => a.workRequestNumber.localeCompare(b.workRequestNumber),
    },
    {
      title: "Work Request Title",
      dataIndex: "workRequestTitle",
      key: "workRequestTitle",
      width: 280,
      render: (text, record) => (
        <div>
          <Link to={`/work-request-view/${record.id}`} className="fw-bold text-primary">
            {text}
          </Link>
          <div className="text-muted small mt-1">
            {record.siteName}
            {record.projectName && ` • ${record.projectName}`}
          </div>
        </div>
      ),
    },
    {
      title: "Request For",
      dataIndex: "requestFor",
      key: "requestFor",
      width: 140,
      render: (text) => (
        <Tag color={getRequestForColor(text)}>{text}</Tag>
      ),
      filters: [
        { text: "Construction Work", value: "Construction Work" },
        { text: "Service", value: "Service" },
        { text: "Maintenance", value: "Maintenance" },
        { text: "Others", value: "Others" },
      ],
      onFilter: (value, record) => record.requestFor === value,
    },
    {
      title: "Work Lines",
      dataIndex: "totalLines",
      key: "totalLines",
      width: 100,
      align: "center",
      render: (text) => (
        <Badge count={text} color="#1890ff" />
      ),
      sorter: (a, b) => a.totalLines - b.totalLines,
    },
    {
      title: "Planned Dates",
      key: "plannedDates",
      width: 160,
      render: (_, record) => (
        <div>
          <div className="small">
            <strong>Start:</strong> {dayjs(record.plannedStartDate).format("DD-MMM-YYYY")}
          </div>
          <div className="small">
            <strong>End:</strong> {dayjs(record.plannedEndDate).format("DD-MMM-YYYY")}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
      filters: [
        { text: "Draft", value: "Draft" },
        { text: "Pending Approval", value: "Pending Approval" },
        { text: "Approved", value: "Approved" },
        { text: "Rejected", value: "Rejected" },
        { text: "In Progress", value: "In Progress" },
        { text: "Completed", value: "Completed" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Approval Required",
      dataIndex: "approvalRequired",
      key: "approvalRequired",
      width: 120,
      align: "center",
      render: (required) => (
        <Tag color={required ? "orange" : "green"}>
          {required ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (text, record) => (
        <div>
          <div className="small">{dayjs(text).format("DD-MMM-YYYY")}</div>
          <div className="text-muted small">by {record.createdBy}</div>
        </div>
      ),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 280,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            size="small"
            icon={<Icon name="eye" />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          
          {record.status === "Draft" && (
            <>
              <Button
                type="link"
                size="small"
                icon={<Icon name="edit" />}
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>
              <Button
                type="link"
                size="small"
                icon={<Icon name="copy" />}
                onClick={() => handleDuplicate(record)}
              >
                Duplicate
              </Button>
              {record.totalLines > 0 && (
                <Button
                  type="link"
                  size="small"
                  icon={<Icon name="send" />}
                  onClick={() => handleSubmitForApproval(record)}
                >
                  Submit
                </Button>
              )}
            </>
          )}
          
          {record.status === "Pending Approval" && (
            <>
              <Button
                type="link"
                size="small"
                style={{ color: "#28a745" }}
                icon={<Icon name="check" />}
                onClick={() => handleApprove(record)}
              >
                Approve
              </Button>
              <Button
                type="link"
                size="small"
                danger
                icon={<Icon name="cross" />}
                onClick={() => handleReject(record)}
              >
                Reject
              </Button>
            </>
          )}
          
          {(record.status === "Draft" || record.status === "Rejected") && (
            <Button
              type="link"
              size="small"
              danger
              icon={<Icon name="trash" />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleFiltersChange = (newFilters: WorkRequestFilters) => {
    setFilters(newFilters);
  };

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-request-list"
      title="Work Request Management"
      columns={columns}
      dataSource={workRequests}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkRequestAside 
            updateSm={setSm} 
            sm={sm} 
            onFiltersChange={handleFiltersChange}
            filters={filters}
          />
        </div>
      }
      // topRightJSX={
      //   <Link to="/work-request-creation">
      //     <ReactstrapButton color="primary">
      //       <Icon name="plus" />
      //       Create Work Request
      //     </ReactstrapButton>
      //   </Link>
      // }
      extra={{
        tableScrollX: 1400,
        showSearch: true,
        searchPlaceholder: "Search by WR#, Title, or Creator...",
        onSearch: (value:any) => {
          handleFiltersChange({ ...filters, searchText: value });
        }
      }}
    />
  );
};

export default WorkRequestList;